import { useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { ToastMessage } from '../toast';
import { isRecord, toStringValue } from '../api/typeHelpers';
import { useAuth } from '../context/AuthContext';
import { getErrorCodeMessage } from '../api/structs';
import { notifyMediaLibraryInvalidated } from './mediaLibraryEvents';
import {
  handleRenderProgressEvent,
  markClipRenderComplete,
} from '../components/clips/renderState';
import {
  applyTelegramSendEvent,
  TelegramSendEvent,
} from '../components/clips/telegramSendState';
import { friendlyRenderError } from '../components/clips/persistenceLabels';
import { clipStyleLabel } from '../components/clips/clipStyleDisplay';
import {
  getClipEditorState,
  refreshClip,
  stageReelReviewSuggestions,
} from '../components/clip-editor/editorStore';
import {
  castUpdateFromEvent,
  publishCastUpdate,
} from '../components/ideas/pipeline-run/cast/castEvents';
import { publishRunLifecycle, runLifecycleFromEvent } from './runLifecycleEvents';

// All known backend webhook events
export type WebSocketEventType =
  | 'AddPromptIdea'
  | 'EditPromptIdea'
  | 'DeletePromptIdea'
  | 'UpdateClipPrompt'
  | 'UpdateClipPromptFileURL'
  | 'UpdateMediaItemFileURL'   // image/video/audio generation done, lip sync done
  | 'AddMediaItem'
  | 'DeleteMediaItem'
  | 'UpdateMediaItemMetadata'
  | 'ReplaceMediaItemMetadata'
  | 'run_update'
  | 'PipelineStarted'
  | 'CheckpointStarted'
  | 'CheckpointCompleted'
  | 'CheckpointFailed'
  | 'PipelinePaused'
  | 'PipelineCompleted'
  | 'PipelineCancelled'
  | 'DistributorCompleted'
  | 'ReviewVerdict'
  | 'ReviewTrace'
  | 'AgentProgress'          // an agent checkpoint finished a turn or a tool call
  | 'MediaReviewVerdict'
  | 'GeneratorProgress'
  | 'RenderProgress'
  | 'ReelReviewCompleted'
  | 'MediaSwapped'            // clip media slot rebound (Replace / alternate pick)
  | 'CastUpdated'             // a run's cast registry changed (whole list on the event)
  | 'TelegramSend'            // background video send: queued/compressing/uploading/sent/failed
  | 'PipelineGraphChanged'    // a director run's graph grew or changed
  | 'PipelineAwaitingDirector' // a director run is waiting on the director or the user
  | 'ManifestClipCreated'     // a root run's final output_contract manifest became a clip
  | 'ManifestClipFailed'      // the clip-from-manifest hook failed; the run itself is unaffected
  | string;


const getEventPayload = (value: unknown): Record<string, unknown> => {
  if (!isRecord(value)) return {};
  return isRecord(value.data) ? value.data : value;
};

const isSchedulerRunUpdate = (payload: Record<string, unknown>): boolean => {
  const explicitType = toStringValue(payload.type).toLowerCase();
  if (explicitType === 'run_update') {
    return true;
  }

  const eventType = toStringValue(payload.event_type).toLowerCase();
  if (eventType.startsWith('run.')) {
    return true;
  }

  const context = [
    payload.kind,
    payload.type,
    payload.run_type,
    payload.scope,
    payload.domain,
    payload.owner,
    payload.source,
    payload.message,
  ]
    .map((item) => toStringValue(item).toLowerCase())
    .join(' ');

  return (
    context.includes('scheduler') ||
    context.includes('schedule') ||
    context.includes('publish') ||
    context.includes('posting')
  );
};

export const buildRunUpdateToast = (rawEvent: unknown): ToastMessage | null => {
  const payload = getEventPayload(rawEvent);
  if (!isSchedulerRunUpdate(payload)) return null;

  const status = toStringValue(
    payload.status ?? payload.run_status ?? payload.state
  ).toLowerCase();
  const message =
    toStringValue(payload.message) ||
    toStringValue(payload.detail) ||
    toStringValue(payload.error);

  if (status === 'completed' || status === 'succeeded' || status === 'success') {
    return {
      text: message || 'Scheduling run completed.',
      level: 'success',
    };
  }

  if (status === 'failed' || status === 'error') {
    return {
      text: message || 'Scheduling run failed.',
      level: 'error',
    };
  }

  if (status === 'cancelled' || status === 'canceled') {
    return {
      text: message || 'Scheduling run cancelled.',
      level: 'warning',
    };
  }

  if (status === 'queued' || status === 'pending' || status === 'running' || status === 'started') {
    return {
      text: message || 'Scheduling run in progress...',
      level: 'info',
    };
  }

  if (!status && message) {
    return {
      text: message,
      level: 'info',
    };
  }

  return null;
};

/** A toast has one line to work with; the full reason renders on the scene. */
const TOAST_REASON_MAX = 160;

const capToastReason = (reason: string): string =>
  reason.length > TOAST_REASON_MAX ? `${reason.slice(0, TOAST_REASON_MAX).trimEnd()}…` : reason;

/**
 * Names WHAT failed and WHY for a media item that just landed on the `_failed`
 * placeholder.
 *
 * This toast used to read "Generation failed" and nothing else — no scene, no
 * reason, and (for a regenerate) fired against a throwaway VARIANT id no view
 * has ever heard of, which made it the single least actionable message in the
 * app. `variant_of` is the tell: only a regeneration candidate carries it, and
 * its failure is a failed REGENERATE of the item it names.
 */
export const buildMediaFailedToast = (
  metadata: Record<string, unknown>
): ToastMessage => {
  const isRegenerate = Boolean(toStringValue(metadata.variant_of));
  const headline = isRegenerate ? 'Regeneration failed' : 'Generation failed';
  const raw = toStringValue(metadata.persistence_error);
  if (!raw) return { text: headline, level: 'error' };
  return { text: `${headline}: ${capToastReason(friendlyRenderError(raw))}`, level: 'error' };
};

/**
 * Builds the toast for a CheckpointFailed event, or null to suppress it. A
 * child-scoped failure (carrying `parent_run_id`, emitted for a distributor
 * fan-out child) is suppressed here because it renders inline on its per-slot
 * review card; a top-level failure keeps the global warning toast.
 */
export const buildCheckpointFailedToast = (
  payload: Record<string, unknown>
): ToastMessage | null => {
  if (toStringValue(payload.parent_run_id)) return null;
  const code = typeof payload.error_code === 'number' ? payload.error_code : undefined;
  const message = getErrorCodeMessage(code);
  return { text: message ?? 'A checkpoint failed.', level: 'warning' };
};

interface EventRefreshTargets {
  refreshIdeas: boolean;
  refreshClips: boolean;
}

const PIPELINE_LIFECYCLE_EVENTS = new Set<string>([
  'PipelineStarted',
  'CheckpointStarted',
  'CheckpointCompleted',
  'CheckpointFailed',
  'PipelinePaused',
  'PipelineCompleted',
  'PipelineCancelled',
  'DistributorCompleted',
  // A stored verdict rides along on the run itself, so the run poll picks it
  // up exactly like a CheckpointCompleted — no list refresh, no extra fetch.
  'ReviewVerdict',
  // The trace panel fetches its list lazily (on open), so this never needs a
  // refetch either. The event itself is not consumed yet (see the no-op case
  // below) — live-append into an OPEN panel is deferred.
  'ReviewTrace',
  'GeneratorProgress',
  // The agent's log rides on the run (refetched via RUN_STATE_EVENTS).
  'AgentProgress',
  // A director run's graph view is read by its own readers (RUN_STATE_EVENTS).
  'PipelineGraphChanged',
  'PipelineAwaitingDirector',
]);

export const getEventRefreshTargets = (eventType: string): EventRefreshTargets => {
  if (eventType === 'AddPromptIdea' || eventType === 'EditPromptIdea' || eventType === 'DeletePromptIdea') {
    return { refreshIdeas: true, refreshClips: false };
  }

  if (
    eventType === 'UpdateClipPrompt' ||
    eventType === 'UpdateClipPromptFileURL' ||
    eventType === 'UpdateMediaItemFileURL' ||
    eventType === 'AddMediaItem' ||
    eventType === 'DeleteMediaItem' ||
    eventType === 'UpdateMediaItemMetadata' ||
    eventType === 'ReplaceMediaItemMetadata' ||
    eventType === 'run_update' ||
    // The clip-side reviewer's verdict lands in metadata on the media item
    // itself (not on a run's checkpoint result), so — unlike the pipeline
    // ReviewVerdict below — this one needs the clip refetched to pick it up.
    eventType === 'MediaReviewVerdict' ||
    // A swap rebinds a clip slot to a DIFFERENT media item without touching
    // any item's bytes or metadata, so none of the media-item events above
    // fire for it — without this branch a run-origin alternate pick was
    // invisible outside the tab that made it until the 30s poll.
    eventType === 'MediaSwapped' ||
    // a new clip exists: the clips list must show it
    eventType === 'ManifestClipCreated'
  ) {
    return { refreshIdeas: false, refreshClips: true };
  }

  if (PIPELINE_LIFECYCLE_EVENTS.has(eventType)) {
    return { refreshIdeas: false, refreshClips: false };
  }

  return { refreshIdeas: false, refreshClips: false };
};

interface UseWebSocketEventsOptions {
  onRefreshIdeas: () => void;
  onRefreshClips: () => void;
  onToast?: (message: string | ToastMessage) => void;
  onMediaUpdate?: (payload: any) => void;
  /** Fires on every (re)connect of the socket — resync state that a missed event may have left stale. */
  onConnect?: () => void;
}

export function useWebSocketEvents({ onRefreshIdeas, onRefreshClips, onToast, onMediaUpdate, onConnect }: UseWebSocketEventsOptions) {
  const { user } = useAuth();
  const currentUserID = user?.user_id ?? null;

  const handleEvent = useCallback((eventType: WebSocketEventType, data: any) => {
    // Drop events tagged for a different user. Untagged events (user_id absent
    // or empty) are treated as global and pass through. The backend stamps
    // user_id on every per-user mutation via ViewModel.notify.
    const eventUserID = toStringValue((data as Record<string, unknown> | null)?.user_id);
    if (eventUserID && currentUserID && eventUserID !== currentUserID) {
      return;
    }

    // The run store has no socket of its own, so this is where a lifecycle
    // event becomes a refetch of the run it names — the difference between a
    // card that flips the moment a run finishes and one that waits for the
    // next list poll. Publishing to nobody is fine: the poll still carries it.
    const lifecycle = runLifecycleFromEvent(eventType, getEventPayload(data));
    if (lifecycle) publishRunLifecycle(lifecycle);

    const targets = getEventRefreshTargets(eventType);
    if (targets.refreshIdeas) {
      onRefreshIdeas();
    }
    if (targets.refreshClips) {
      onRefreshClips();
    }

    if (
      eventType === 'AddMediaItem' ||
      eventType === 'UpdateMediaItemFileURL' ||
      eventType === 'DeleteMediaItem' ||
      eventType === 'UpdateMediaItemMetadata' ||
      eventType === 'ReplaceMediaItemMetadata'
    ) {
      const payload = getEventPayload(data);
      notifyMediaLibraryInvalidated({
        eventType,
        mediaId: toStringValue(payload.media_id ?? payload.id) || undefined,
      });
    }

    switch (eventType) {
      case 'AddPromptIdea':
      case 'EditPromptIdea':
      case 'DeletePromptIdea':
        console.log('Idea updated');
        break;

      case 'UpdateClipPrompt':
        console.log('Clip updated');
        break;

      case 'UpdateClipPromptFileURL': {
        const payload = getEventPayload(data);
        const urls: unknown[] = Array.isArray(payload.file_urls) ? payload.file_urls : [];
        const hasRealURL = urls.some((u) => {
          const s = toStringValue(u);
          return s !== '' && !s.includes('_waiting') && !s.includes('_failed');
        });
        if (hasRealURL) {
          // Success is signalled by this event (not a "ready" RenderProgress),
          // so clear any live render bar here.
          markClipRenderComplete(toStringValue(payload.id ?? payload.clip_id));
          onToast?.({ text: 'Render complete ✓', level: 'success' });
        }
        break;
      }

      case 'RenderProgress': {
        const payload = getEventPayload(data);
        handleRenderProgressEvent({
          clip_id: toStringValue(payload.clip_id ?? payload.id),
          status: toStringValue(payload.status),
          progress: typeof payload.progress === 'number' ? payload.progress : undefined,
          stage: toStringValue(payload.stage),
          error: toStringValue(payload.error),
        });
        break;
      }

      case 'UpdateMediaItemFileURL': {
        const payload = getEventPayload(data);
        if (onMediaUpdate) {
          onMediaUpdate(payload);
        }
        const mediaType = toStringValue(payload.type);
        const metadata = (payload.metadata ?? {}) as Record<string, unknown>;
        const isLipSynced = Boolean(metadata.lip_synced);
        const fileURL = toStringValue(payload.file_url);
        const isFailed = fileURL.includes('_failed');

        if (isFailed) {
          onToast?.(buildMediaFailedToast(metadata));
        } else if (isLipSynced) {
          onToast?.({ text: 'Lip sync complete ✓', level: 'success' });
        } else if (mediaType === 'ai_video') {
          onToast?.({ text: 'Video ready ✓', level: 'success' });
        } else if (mediaType === 'image') {
          onToast?.({ text: 'Image ready ✓', level: 'success' });
        } else if (mediaType === 'audio') {
          onToast?.({ text: 'Audio ready ✓', level: 'success' });
        }
        break;
      }

      case 'run_update': {
        const toast = buildRunUpdateToast(data);
        if (toast) {
          onToast?.(toast);
        }
        break;
      }

      case 'PipelineCompleted':
        onToast?.({ text: 'Pipeline completed.', level: 'success' });
        break;

      case 'PipelinePaused':
        onToast?.({ text: 'Pipeline paused for review.', level: 'info' });
        break;

      case 'CheckpointFailed': {
        const toast = buildCheckpointFailedToast(getEventPayload(data));
        if (toast) {
          onToast?.(toast);
        }
        break;
      }

      case 'PipelineCancelled':
        onToast?.({ text: 'Pipeline cancelled.', level: 'warning' });
        break;

      // The run refetch (RUN_STATE_EVENTS) is what puts manifest_clip_id on the
      // finished thread; this only tells the user where to look. The style is
      // named by its display label — no style is hard-wired here.
      case 'ManifestClipCreated': {
        const payload = getEventPayload(data);
        const label = clipStyleLabel(toStringValue(payload.clip_style));
        onToast?.({
          text: `Your ${label ? `${label} ` : ''}clip is ready — open it from the finished thread.`,
          level: 'success',
        });
        break;
      }

      case 'ManifestClipFailed': {
        const payload = getEventPayload(data);
        const reason = toStringValue(payload.error).trim() || 'unknown error';
        onToast?.({ text: `The run's clip could not be created: ${capToastReason(reason)}`, level: 'error' });
        break;
      }

      // Handled by the run refetch (see PIPELINE_LIFECYCLE_EVENTS): the verdict
      // is already on the run's checkpoint result by the time this arrives, and
      // the badge renders from there. No toast — a passing review is noise, and
      // a failing one is visible on the step it belongs to.
      case 'ReviewVerdict':
        break;

      // No panel state to push into — the trace panel fetches its list lazily on open.
      case 'ReviewTrace':
        break;

      // The agent card reads its log off the refetched run; no toast.
      case 'AgentProgress':
        break;

      // The director graph's readers refetch it from the lifecycle publish above; no toast.
      case 'PipelineGraphChanged':
      case 'PipelineAwaitingDirector':
        break;

      // Handled by the refreshClips branch above: the clip refetch picks up
      // the new metadata.review_verdict/review_variants/review_log on the
      // media item, and the ReviewerPanel badge/panel render from there.
      case 'MediaReviewVerdict':
        break;

      // Unlike the per-scene verdict above, the OPEN editor's own state (not
      // just the top-level clip list) needs this: the report only reaches
      // `clipMetadata.reel_review` via a refetch, and staging it (auto-apply +
      // the stale-hash check) is a one-shot action that must run exactly once,
      // right after that refetch lands — never from a generic refresh.
      // The clips-list refetch rides the refreshClips branch above; this case
      // is for the OPEN editor, whose store fetches its clip itself. A swap is
      // the one clip mutation no media-item event announces (the items are
      // untouched, only the clip's ref moved), so a storyboard pick — or one
      // from another tab — must push the editor onto the new binding here or
      // it plays the old take until the next poll.
      case 'MediaSwapped': {
        const payload = getEventPayload(data);
        const clipId = toStringValue(payload.clip_id ?? payload.id);
        const openClipId = getClipEditorState().clipId;
        if (clipId && openClipId && clipId === openClipId) {
          void refreshClip();
        }
        break;
      }

      case 'ReelReviewCompleted': {
        const payload = getEventPayload(data);
        const clipId = toStringValue(payload.clip_id ?? payload.id);
        const openClipId = getClipEditorState().clipId;
        if (clipId && openClipId && clipId === openClipId) {
          // The user can switch clips while this refetch is in flight —
          // `stageReelReviewSuggestions` re-checks `clipId` against whatever
          // is open by the time the refetch resolves, so a report for THIS
          // clip can never land staged on a different one opened meanwhile.
          void refreshClip().then(() => stageReelReviewSuggestions(clipId));
        }
        break;
      }

      // The cast rides on the event's own `data`, so this needs no refetch:
      // the mounted panel for that run replaces its list and every other one
      // ignores it. A cast panel is not always mounted — publishing to nobody
      // is fine, the next run read carries the same list.
      case 'CastUpdated': {
        const update = castUpdateFromEvent(getEventPayload(data));
        if (update) publishCastUpdate(update);
        break;
      }

      // The send runs on the backend long after the POST returned, so this
      // event is the ONLY thing that reports how it ended. Without it the
      // button would spin until the page was reloaded.
      case 'TelegramSend': {
        const payload = getEventPayload(data) as TelegramSendEvent;
        applyTelegramSendEvent(payload);
        if (payload.status === 'sent') {
          onToast?.({ text: 'Video sent to Telegram', level: 'success' });
        } else if (payload.status === 'failed') {
          onToast?.({
            text: `Telegram: ${payload.error || 'send failed'}`,
            level: 'error',
          });
        }
        break;
      }

      default:
        console.log('Unknown event:', eventType);
    }
  }, [onRefreshIdeas, onRefreshClips, onToast, onMediaUpdate, currentUserID]);

  useWebSocket({ onEvent: handleEvent, onOpen: onConnect });
}
